/**
 * Upcoming Events Component
 * Displays upcoming events with date filters
 * Enhanced for Timeline tab integration
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, ChevronRight, Clock, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { getEventColor, getEventIcon } from '@/lib/eventColors';
import { useEventColors } from '@/context/EventColorContext';
import { useDateFormat } from '@/context/DateFormatContext';

// =====================
// Types
// =====================

export interface Event {
  id: string;
  title: string;
  type: string;
  start_date: string;
  end_date?: string;
  status: string;
  location?: any;
  color?: string;
  venue_name?: string;
  city?: string;
  state?: string;
}

export type DateFilter = 'upcoming' | 'past' | 'today' | 'tomorrow' | 'week' | 'month' | 'all';

export interface UpcomingEventsProps {
  events?: Event[];
  selectedEventId?: string | null;
  dateFilter?: DateFilter;
  onDateFilterChange?: (filter: DateFilter) => void;
  onEventSelect?: (eventId: string) => void;
  onRefresh?: () => void;
  limit?: number;
  showViewAll?: boolean;
  showFilters?: boolean;
  compact?: boolean;
}

// =====================
// Helper Functions
// =====================

function filterEventsByDate(events: Event[], filter: DateFilter): Event[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const endOfWeek = new Date(today);
  endOfWeek.setDate(endOfWeek.getDate() + 7);

  const endOfMonth = new Date(today);
  endOfMonth.setMonth(endOfMonth.getMonth() + 1);

  return events
    .filter(event => {
      const eventDate = new Date(event.start_date);
      eventDate.setHours(0, 0, 0, 0);

      switch (filter) {
        case 'upcoming':
          return eventDate >= today;
        case 'past':
          return eventDate < today;
        case 'today':
          return eventDate.getTime() === today.getTime();
        case 'tomorrow':
          return eventDate.getTime() === tomorrow.getTime();
        case 'week':
          return eventDate >= today && eventDate < endOfWeek;
        case 'month':
          return eventDate >= today && eventDate < endOfMonth;
        case 'all':
        default:
          return true; // show all events including past
      }
    })
    .sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());
}

function getFilterLabel(filter: DateFilter): string {
  const labels: Record<DateFilter, string> = {
    upcoming: 'Upcoming',
    past: 'Past',
    today: 'Today',
    tomorrow: 'Tomorrow',
    week: 'This Week',
    month: 'This Month',
    all: 'All'
  };
  return labels[filter];
}

function formatEventDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);

  // Check if today
  if (date.toDateString() === now.toDateString()) {
    return 'Today';
  }
  // Check if tomorrow
  if (date.toDateString() === tomorrow.toDateString()) {
    return 'Tomorrow';
  }
  // Otherwise use standard format
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    weekday: 'short'
  });
}

// =====================
// Component
// =====================

export function UpcomingEvents({
  events: externalEvents,
  selectedEventId,
  dateFilter = 'upcoming',
  onDateFilterChange,
  onEventSelect,
  onRefresh,
  limit = 10,
  showViewAll = true,
  showFilters = true,
  compact = false
}: UpcomingEventsProps) {
  const navigate = useNavigate();
  const { eventTypeColors } = useEventColors();
  const { formatDate: formatDateGlobal, settings } = useDateFormat();

  const { data: fetchedData = [], isLoading: isPending } = useQuery({
    queryKey: queryKeys.allEventsAndTours,
    queryFn: async () => {
      const result = await apiGet<Event[]>('/events', { limit: 100 });
      if (!result.success) throw new Error(result.error || 'Failed to load events');
      const allData = result.data;
      const fetched: Event[] = Array.isArray(allData) ? allData :
                               allData && Array.isArray((allData as any).data) ? (allData as any).data :
                               allData && Array.isArray((allData as any).events) ? (allData as any).events : [];
      return fetched;
    },
    enabled: !externalEvents,
    // Phase 1 fix: staleTime: 0 caused mount-triggered refetches to race with
    // optimistic deletes. 30s aligns with sibling dashboard queries.
    staleTime: 30000,
  });

  const isLoading = !externalEvents && isPending;
  const allEvents = Array.isArray(externalEvents) ? externalEvents : fetchedData;

  // Filter and sort events
  const filteredEvents = useMemo(() => {
    return filterEventsByDate(allEvents, dateFilter).slice(0, limit);
  }, [allEvents, dateFilter, limit]);

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const hours = date.getHours();
    const minutes = date.getMinutes();

    if (minutes === 0 && hours === 0) {
      return ''; // No time specified
    }

    if (settings.timeFormat === '12h') {
      const period = hours >= 12 ? 'PM' : 'AM';
      const hours12 = hours % 12 || 12;
      return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
    } else {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-16 bg-gray-100 dark:bg-gray-800 rounded-md"></div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Filter Tabs */}
      {showFilters && (
        <div className="flex border-b border-gray-200 dark:border-gray-700 mb-3 overflow-x-auto">
          {(['upcoming', 'past', 'today', 'tomorrow', 'week', 'month', 'all'] as const).map((filter) => (
            <button
              key={filter}
              onClick={() => onDateFilterChange?.(filter)}
              className={`px-3 py-2.5 text-xs font-medium whitespace-nowrap transition-colors ${
                dateFilter === filter
                  ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              {getFilterLabel(filter)}
            </button>
          ))}
        </div>
      )}

      {/* Events List */}
      {filteredEvents.length === 0 ? (
        <div className="text-center py-8 px-4 text-gray-500">
          <Calendar className="w-8 h-8 mx-auto mb-2 text-gray-400" />
          <p className="text-sm">No events</p>
          <p className="text-xs text-gray-400">in {getFilterLabel(dateFilter).toLowerCase()}</p>
        </div>
      ) : (
        <div className={compact ? 'flex flex-col gap-0.5 flex-1 overflow-y-auto pb-2' : 'space-y-3 flex-1 overflow-y-auto pb-2'}>
          {filteredEvents.map((event) => {
            const colorConfig = getEventColor(event.type, event.color, eventTypeColors);
            const EventIcon = getEventIcon(event.type);
            const isSelected = event.id === selectedEventId;

            if (compact) {
              return (
                <button
                  key={event.id}
                  onClick={() => onEventSelect?.(event.id)}
                  className={`w-full flex items-center gap-2 text-left px-1.5 py-1.5 rounded transition-colors cursor-pointer focus:outline-none ${
                    isSelected
                      ? 'text-blue-600 dark:text-blue-400'
                      : 'text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <div
                    className={`p-1.5 rounded-md flex-shrink-0 ${colorConfig.bgLight || ''} ${colorConfig.text || ''}`}
                    style={{
                      ...(colorConfig.style?.bgLight ? { backgroundColor: colorConfig.style.bgLight } : {}),
                      ...(colorConfig.style?.text ? { color: colorConfig.style.text } : {}),
                    }}
                  >
                    <EventIcon className="w-3.5 h-3.5" />
                  </div>
                  <span className="flex-1 min-w-0 truncate text-sm">{event.title}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                    {formatEventDate(event.start_date)}
                  </span>
                </button>
              );
            }

            const getStatusBadge = (status: string) => {
              switch (status?.toLowerCase()) {
                case 'completed': return { bg: 'bg-green-100 dark:bg-green-900/40', text: 'text-green-700 dark:text-green-400', label: 'Completed' };
                case 'confirmed': return { bg: 'bg-blue-100 dark:bg-blue-900/40', text: 'text-blue-700 dark:text-blue-400', label: 'Confirmed' };
                case 'scheduled': return { bg: 'bg-purple-100 dark:bg-purple-900/40', text: 'text-purple-700 dark:text-purple-400', label: 'Scheduled' };
                case 'in_progress': return { bg: 'bg-yellow-100 dark:bg-yellow-900/40', text: 'text-yellow-700 dark:text-yellow-400', label: 'In Progress' };
                case 'cancelled': return { bg: 'bg-red-100 dark:bg-red-900/40', text: 'text-red-700 dark:text-red-400', label: 'Cancelled' };
                default: return { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-700 dark:text-gray-300', label: 'Pending' };
              }
            };
            const badge = getStatusBadge(event.status);

            return (
              <button
                key={event.id}
                onClick={() => onEventSelect?.(event.id)}
                className={`
                  w-full text-left rounded-lg border transition-all cursor-pointer overflow-hidden focus:outline-none
                  ${isSelected
                    ? 'border-blue-500 shadow-lg ring-1 ring-blue-500 bg-white dark:bg-gray-900'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-md'
                  }
                `}
              >
                {/* Card Header */}
                <div className="p-3 border-b border-gray-100 dark:border-gray-800">
                  <div className="flex items-start space-x-2 flex-1 min-w-0">
                    <div
                      className={`p-2 rounded-lg flex-shrink-0 ${colorConfig.bgLight || ''} ${colorConfig.text || ''}`}
                      style={{
                        ...(colorConfig.style?.bgLight ? { backgroundColor: colorConfig.style.bgLight } : {}),
                        ...(colorConfig.style?.text ? { color: colorConfig.style.text } : {}),
                      }}
                    >
                      <EventIcon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                        {event.title}
                      </h4>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${badge.bg} ${badge.text}`}>
                          {badge.label}
                        </span>
                      </div>
                    </div>
                    {isSelected && (
                      <ChevronRight className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-2" />
                    )}
                  </div>
                </div>

                {/* Card Details */}
                <div className="p-3 space-y-2">
                  {/* Date */}
                  <div className="flex items-center space-x-2 text-xs text-gray-600 dark:text-gray-400">
                    <Clock className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                    <span className="font-medium">{formatEventDate(event.start_date)}</span>
                    {formatTime(event.start_date) && (
                      <span className="text-gray-500 dark:text-gray-500">• {formatTime(event.start_date)}</span>
                    )}
                  </div>

                  {/* Location */}
                  {(event.venue_name || event.city) && (
                    <div className="flex items-start space-x-2 text-xs text-gray-600 dark:text-gray-400">
                      <MapPin className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        {event.venue_name && (
                          <p className="font-medium truncate">{event.venue_name}</p>
                        )}
                        {event.city && (
                          <p className="text-gray-500 dark:text-gray-500 truncate">
                            {event.city}{event.state ? `, ${event.state}` : ''}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* View All Button */}
      {showViewAll && (
        <button
          onClick={() => navigate('/calendar')}
          className="w-full mt-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
        >
          View Full Calendar →
        </button>
      )}
    </div>
  );
}
